import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type FeedType = 'my-feed' | 'popular' | 'upvoted' | 'latest' | 'discussed' | 'hot';
type Category = 'all' | 'cars' | 'motorcycles' | 'scooters' | 'ev' | 'adv' | 'motorsport' | 'launches';
type ContentType = 'all' | 'article' | 'video' | 'discussion';
type ViewMode = 'cards' | 'list' | 'magazine';
type SourceMode = 'following' | 'all';
type Tag = string | null;
type Language = 'all' | 'english' | 'hindi' | 'hinglish';
type FuelType = 'all' | 'ev' | 'phev' | 'hybrid' | 'flex-fuel' | 'petrol' | 'diesel';
type Theme = 'dark' | 'light' | 'system';

/**
 * T-177: light theme is shipped BROKEN — the palette is only defined for dark,
 * so light renders pale-gray body text on white, a dark sidebar beside a white
 * main column, and washed-out chips. The operator's ruling is to REMOVE the
 * control rather than half-define a palette, because a theme that is 80%
 * defined fails exactly where nobody screenshots.
 *
 * REMOVING THE TWO BUTTONS IS NOT ENOUGH, and this is the part a "pull the
 * toggle" diff misses: `theme` is PERSISTED to localStorage. Every visitor who
 * already clicked light — or who is on 'system' with an OS set to light — has
 * `light` on disk right now and would rehydrate straight back into the broken
 * rendering on their next visit, with the control gone and NO WAY to get out.
 * Pulling the control alone would have made those users permanently stuck.
 *
 * So resolution is forced to dark at the single choke point every path runs
 * through. The stored value is left untouched on purpose: the token work is
 * unbundled, not cancelled (its own row), and when it lands, deleting this
 * clamp restores each user's real preference intact.
 */
function applyTheme(_theme: Theme) {
  const resolved: 'dark' = 'dark';
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', '#0e1217');
}

let systemListener: (() => void) | null = null;

function setupSystemListener(store: { getState: () => { theme: Theme } }) {
  if (systemListener) { systemListener(); systemListener = null; }
  if (store.getState().theme !== 'system') return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => applyTheme('system');
  mq.addEventListener('change', handler);
  systemListener = () => mq.removeEventListener('change', handler);
}

interface UiState {
  // Feed preferences
  feedType: FeedType;
  category: Category;
  contentType: ContentType;
  viewMode: ViewMode;
  sourceMode: SourceMode;
  tag: Tag;
  language: Language;
  fuelType: FuelType;

  // Theme
  theme: Theme;

  // Sidebar
  sidebarCollapsed: boolean;

  // Search
  searchOpen: boolean;
  searchQuery: string;

  // Actions
  setFeedType: (type: FeedType) => void;
  setCategory: (category: Category) => void;
  setContentType: (type: ContentType) => void;
  setViewMode: (mode: ViewMode) => void;
  setSourceMode: (mode: SourceMode) => void;
  setTag: (tag: Tag) => void;
  setLanguage: (language: Language) => void;
  setFuelType: (fuelType: FuelType) => void;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSearchOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      // Default values
      feedType: 'my-feed',
      category: 'all',
      contentType: 'all',
      viewMode: 'cards',
      sourceMode: 'all',
      tag: null,
      language: 'all',
      fuelType: 'all',
      theme: 'dark',
      sidebarCollapsed: false,
      searchOpen: false,
      searchQuery: '',

      // Actions
      setFeedType: (feedType) => set({ feedType }),
      setCategory: (category) => set({ category }),
      setContentType: (contentType) => set({ contentType }),
      setViewMode: (viewMode) => set({ viewMode }),
      setSourceMode: (sourceMode) => set({ sourceMode }),
      setTag: (tag) => set({ tag }),
      setLanguage: (language) => set({ language }),
      setFuelType: (fuelType) => set({ fuelType }),
      toggleTheme: () => set((state) => {
        const next = state.theme === 'dark' ? 'light' : state.theme === 'light' ? 'system' : 'dark';
        applyTheme(next);
        return { theme: next };
      }),
      setTheme: (theme: Theme) => set(() => {
        applyTheme(theme);
        return { theme };
      }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setSearchOpen: (searchOpen) => set({ searchOpen }),
      setSearchQuery: (searchQuery) => set({ searchQuery }),
    }),
    {
      name: 'motowalla-ui',
      partialize: (state) => ({
        feedType: state.feedType,
        category: state.category,
        contentType: state.contentType,
        viewMode: state.viewMode,
        sourceMode: state.sourceMode,
        tag: state.tag,
        language: state.language,
        fuelType: state.fuelType,
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
          setupSystemListener(useUiStore);
        }
      },
    }
  )
);

// Re-attach system listener whenever theme preference changes
useUiStore.subscribe(
  (state, prev) => { if (state.theme !== prev.theme) setupSystemListener(useUiStore); }
);
